import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {ENV_TOKEN, EnvInterface} from 'env-config';
import {firstValueFrom} from 'rxjs';
import {AuthValidationService} from './auth-validation.service';

@Injectable({providedIn: 'root'})
export class ExternalRedirect {

  private http = inject(HttpClient);
  private environment = inject(ENV_TOKEN) as EnvInterface;
  private authValidationService = inject(AuthValidationService);

  ssoRedirect(url: string, replace = false): void {
    this.buildSsoUrl(url).then(ssoUrl => {
      if (replace) {
        window.location.replace(ssoUrl);
      } else {
        window.location.href = ssoUrl;
      }
    });
  }

  /**
   * Adds a one-minute SSO token to a main-site link, so the user arrives signed in. On any failure it
   * returns the plain link: the user still gets there, just signed out, so nothing here may block them.
   *
   * /auth/sso-token refuses an expired access token outright (strict expiry), and the token can lapse
   * between validateToken() below and that call. No retry is needed here: authRefreshInterceptor
   * refreshes and retries a 401 once, for every bearer call to the API including this one. This class
   * used to do the same itself, which doubled the calls on a 401. Keep /auth/sso-token out of the
   * interceptor's EXCLUDED_AUTH_PATHS; the spec covers this path through the interceptor.
   */
  private async buildSsoUrl(destination: string): Promise<string> {
    const validation = await firstValueFrom(this.authValidationService.validateToken());
    if (!validation.isValid) return destination;

    try {
      return await this.requestSsoUrl(destination);
    } catch (err) {
      this.warnSignedOutFallback(err);
      return destination;
    }
  }

  private async requestSsoUrl(destination: string): Promise<string> {
    const token = this.authValidationService.getStoredToken();
    const decodedUser = this.authValidationService.getDecodedUser();
    if (!token || !decodedUser) {
      throw new Error('no usable access token');
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': this.environment.security.tuatara.apiKey,
      'Authorization': `Bearer ${token}`,
    });

    const url = `${this.environment.security.tuatara.endpoint}/auth/sso-token`;
    const res = await firstValueFrom(
      this.http.post<{ accessToken: string; refreshToken?: string }>(`${url}`, decodedUser, {headers})
    );

    const ssoToken = res?.accessToken;
    if (!ssoToken) {
      throw new Error('no token in /auth/sso-token response');
    }

    const sep = destination.includes('?') ? '&' : '?';
    return `${destination}${sep}sso=${encodeURIComponent(ssoToken)}`;
  }

  /**
   * Makes the signed-out fallback identifiable. The API's 401 body carries a reason (INVALID, EXPIRED,
   * NO_EXPIRY, NO_JTI, REVOKED), logged here alongside the API's own info log of the same refusal.
   */
  private warnSignedOutFallback(err: unknown): void {
    const reason = err instanceof HttpErrorResponse
      ? (err.error?.reason ?? `HTTP ${err.status}`)
      : (err instanceof Error ? err.message : String(err));
    console.warn('SSO to the main site failed; continuing signed out:', reason);
  }
}
